import React, { useState } from "react";
import { Loader, SearchCheck } from "lucide-react";
import { toast } from "react-hot-toast";
import { useRunOrphanedPayoutCheck } from "../hooks/useTreasury";

// Detection only - a consumer withdrawal debited the personal wallet, but
// the corresponding business-side payout was never created (the
// fire-and-forget HTTP call that creates it failed silently). This never
// auto-retries or auto-refunds - see the backend's
// OrphanedPayoutReconciliationService for why both are riskier than
// leaving it to a human who can check the provider's own dashboard first.
// Also runs automatically every 30 minutes and emails ops@ - this panel is
// for checking on demand, e.g. right after a support ticket comes in.
function OrphanedPayoutsPanel() {
  const { mutate: runCheck, isPending } = useRunOrphanedPayoutCheck();
  const [results, setResults] = useState(null);
  const [hasRun, setHasRun] = useState(false);

  const handleCheck = () => {
    runCheck(undefined, {
      onSuccess: (data) => {
        setResults(data);
        setHasRun(true);
        if (data.length > 0) {
          toast.error(`Found ${data.length} orphaned debit(s)`);
        } else {
          toast.success("No orphaned debits found");
        }
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Check failed");
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="p-4 text-sm text-gray-600 bg-gray-50 rounded-xl border border-gray-200">
        A consumer withdrawal debits their personal wallet, then makes an unawaited call to debit
        the house-merchant wallet and create the matching business-side payout. If that call
        fails, the personal debit is already committed but nothing else ever happens - this finds
        those cases. Runs automatically every 30 minutes and emails ops when something turns up;
        use the button below to check right now instead of waiting.
      </div>

      <button
        onClick={handleCheck}
        disabled={isPending}
        className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
      >
        {isPending ? <Loader className="w-4 h-4 animate-spin" /> : <SearchCheck className="w-4 h-4" />}
        Check Now
      </button>

      {hasRun && (
        <div className="overflow-hidden rounded border border-gray-200">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                {["Reference", "User", "Amount", "Created", "Last Error"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {results.length > 0 ? (
                results.map((row) => (
                  <tr key={row.transferReference}>
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{row.transferReference}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{row.user?.id ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {row.amount} {row.currency?.currencyCode?.toUpperCase()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600">
                      {row.metadata?.last_retry_error || "—"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                    No orphaned debits found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default OrphanedPayoutsPanel;
